import React, { FC } from 'react';

import { Box, Chip, Divider, Typography } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import ListPage from '../../sharedModules/listPage/ListPage';
import { formatDate } from '../../utils/formatDate';

const formatStatus = (s: string) => s?.split('_').join(' ');

const StepResultsRow: FC<{ data?: any }> = ({ data = {} }) => {
  const steps = data.step_results_titleized ?? {};

  return (
    <Box sx={{ px: 4, py: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Step Results
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {Object.entries(steps).map(([step, result]: [string, any]) => {
        const failed = typeof result === 'object' ? result.status === 'failed' : result === 'failed';
        const error = typeof result === 'object' ? result.error : undefined;
        return (
          <Box
            key={step}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 0.75, borderBottom: '1px solid #eeeeee' }}
          >
            <Typography variant="body2">{step}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {failed ? (
                <>
                  <Chip label="Failed" color="error" size="small" icon={<ErrorIcon />} />
                  {error && (
                    <Typography variant="caption" color="error">
                      {error}
                    </Typography>
                  )}
                </>
              ) : (
                <Chip label="Success" color="success" size="small" icon={<CheckCircleIcon />} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const CallbackResultsPage: FC = () => {
  const defaultFilters = {
    record_type_eq: 'BackflowJobGroup',
    new_status_eq: 'approved',
  };

  const clearFilterSetting = {};

  const callbackColumns = [
    {
      name: 'Order No',
      selector: 'record_identifier',
      grow: 1,
    },
    {
      name: 'Result',
      selector: 'success',
      width: '140px',
      cell: (row: any) => {
        if (row.success === true) return <Chip label="All Steps Passed" color="success" size="small" />;
        if (row.success === false) return <Chip label="Steps Failed" color="error" size="small" />;
        return <Chip label="In Progress" color="warning" size="small" />;
      },
    },
    {
      name: 'Status Transition',
      selector: 'old_status',
      grow: 1.2,
      cell: (row: any) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption">{formatStatus(row.old_status)}</Typography>
          <Typography variant="caption" color="text.secondary">
            →
          </Typography>
          <Typography variant="caption">{formatStatus(row.new_status)}</Typography>
        </Box>
      ),
    },
    {
      name: 'Failed Step',
      selector: 'failed_step',
      grow: 1.5,
      cell: (row: any) =>
        row.failed_step ? (
          <Typography variant="caption" color="error">
            {formatStatus(row.failed_step)}
          </Typography>
        ) : null,
    },
    {
      name: 'Retries',
      selector: 'retry_count',
      width: '80px',
      cell: (row: any) =>
        row.retry_count > 0 ? (
          <Chip label={row.retry_count} color="warning" size="small" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      name: 'Ran At',
      selector: 'created_at',
      grow: 0.8,
      cell: (row: any) => formatDate(row.created_at),
    },
  ];

  return (
    <ListPage
      title="Callback Results"
      titleIcon={<CallIcon />}
      controller="/status_callback_audits"
      columns={callbackColumns}
      defaultFilters={defaultFilters}
      defaultSort="created_at desc"
      clearedFilterSetting={clearFilterSetting}
      expandableRowsComponent={<StepResultsRow />}
    />
  );
};

export default CallbackResultsPage;
